import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';

import { type AdvancedFiltersConfig } from '../advanced-filters';

import {
  type SavedFiltersActionsContextValue,
  type ViewedSavedFilterContextValue,
} from './types';

/**
 *
 * `ViewedSavedFilterContext` is a local context used to keep track of the currently viewed
 * filter across the component.
 */
const ViewedSavedFilterContext =
  createContext<ViewedSavedFilterContextValue | null>(null);

interface SavedFiltersSelectionContextProviderProps extends PropsWithChildren {
  viewedSavedFilterId: string | null;
  setViewedSavedFilterId: (id: string | null) => void;
  editingSavedFilterId: string | null;
  setEditingSavedFilterId: (id: string | null) => void;
}

export const SavedFiltersSelectionContextProvider = ({
  viewedSavedFilterId,
  setViewedSavedFilterId,
  editingSavedFilterId,
  setEditingSavedFilterId,
  children,
}: SavedFiltersSelectionContextProviderProps) => {
  return (
    <ViewedSavedFilterContext.Provider
      value={{
        viewedSavedFilterId,
        setViewedSavedFilterId,
        editingSavedFilterId,
        setEditingSavedFilterId,
      }}
    >
      {children}
    </ViewedSavedFilterContext.Provider>
  );
};

export const useViewedSavedFilterContext = () => {
  const viewedSavedFilterContext = useContext(ViewedSavedFilterContext);

  if (!viewedSavedFilterContext) {
    throw new Error(
      'useViewedSavedFilterContext must be used within a <SavedFiltersSelectionContextProvider />',
    );
  }

  return viewedSavedFilterContext;
};

/**
 * `SavedFiltersContext` makes all required callbacks quickly accessible to all
 * components wrapped by `<SavedFiltersActionsContextProvider />` without prop-drilling.
 */

const SavedFiltersActionsContext =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  createContext<SavedFiltersActionsContextValue<any> | null>(null);

interface SavedFiltersActionsContextProviderProps<
  Config extends AdvancedFiltersConfig = AdvancedFiltersConfig,
> extends PropsWithChildren,
    SavedFiltersActionsContextValue<Config> {}

export const SavedFiltersActionsContextProvider = <
  Config extends AdvancedFiltersConfig,
>({
  children,
  savedFilterTemplates,
  onApplySavedFilter,
  onDeleteSavedFilter,
  onEditSavedFilterName,
  onEditSavedFilter,
  onTogglePinSavedFilter,
}: SavedFiltersActionsContextProviderProps<Config>) => {
  const contextValue: SavedFiltersActionsContextValue<Config> = useMemo(
    () => ({
      savedFilterTemplates,
      onApplySavedFilter,
      onDeleteSavedFilter,
      onEditSavedFilterName,
      onEditSavedFilter,
      onTogglePinSavedFilter,
    }),
    [
      savedFilterTemplates,
      onApplySavedFilter,
      onDeleteSavedFilter,
      onEditSavedFilterName,
      onEditSavedFilter,
      onTogglePinSavedFilter,
    ],
  );

  return (
    <SavedFiltersActionsContext.Provider value={contextValue}>
      {children}
    </SavedFiltersActionsContext.Provider>
  );
};

export const useSavedFiltersActionsContext = <
  Config extends AdvancedFiltersConfig = AdvancedFiltersConfig,
>() => {
  const savedFiltersContext = useContext(SavedFiltersActionsContext);

  if (!savedFiltersContext) {
    throw new Error(
      'useSavedFiltersContext must be used within a <SavedFiltersActionsContextProvider />',
    );
  }

  return savedFiltersContext as SavedFiltersActionsContextValue<Config>;
};
